import {
  LayoutDashboard, Rocket, Building2, Tv, PlusCircle, Settings2, Calendar,
  ListVideo, Wand2, LayoutTemplate, Palette, Package, CreditCard, Settings,
  ServerCog, Repeat, FolderSync, ScrollText,
} from 'lucide-react';
import type { HelpArticle } from './types';

// ─────────────────────────────────────────────────────────────────────────────
// Registro único de artigos da Central de Ajuda do MyShow.
// Cada artigo cobre uma tela. O campo `route`/`routeMatch` casa a ajuda
// contextual (botão "?") com a tela atual.
// ─────────────────────────────────────────────────────────────────────────────

export const HELP_SECTIONS = [
  'Começando',
  'Lojas e TVs',
  'Conteúdo',
  'Produtos',
  'Integração (Agente ETL)',
  'Conta',
] as const;

export const ARTICLES: HelpArticle[] = [
  // ─── COMEÇANDO ────────────────────────────────────────────────────────────
  {
    id: 'dashboard',
    route: '/dashboard',
    section: 'Começando',
    title: 'Dashboard',
    icon: LayoutDashboard,
    summary: 'A visão geral do seu negócio: TVs, horas exibidas e alertas.',
    keywords: ['início', 'home', 'painel', 'resumo', 'visão geral'],
    tourId: 'overview',
    body: [
      { type: 'p', text: 'O Dashboard é a primeira tela após o login. Ele mostra, em tempo real, o que está acontecendo nas suas TVs — quantas estão online, quanto conteúdo foi exibido e se há algum problema que precisa da sua atenção.' },
      { type: 'heading', text: 'O que você vê aqui' },
      { type: 'list', items: [
        '**TVs Online / Offline** — quantas telas estão ligadas e enviando sinal agora.',
        '**Playlists ativas** — conteúdos que estão sendo exibidos.',
        '**Horas de conteúdo exibido** — total de tempo que suas telas ficaram rodando no mês.',
        '**Alertas** — a barra amarela aparece quando uma TV está offline num horário em que normalmente fica ligada.',
        '**Agent** — status da sincronização com o seu sistema/ERP (quando você usa essa integração).',
      ] },
      { type: 'heading', text: 'Ações rápidas' },
      { type: 'steps', items: [
        'Use **Adicionar TV** para conectar uma nova tela.',
        'Use **Nova playlist** para criar um conteúdo do zero.',
        'Clique em qualquer card de TV para ver os detalhes dela.',
      ] },
      { type: 'tip', text: 'A barra de alertas é o melhor lugar para detectar problemas rápido: se uma loja "apagou", ela aparece aqui antes de o cliente reclamar.' },
    ],
  },
  {
    id: 'onboarding',
    route: '/onboarding',
    section: 'Começando',
    title: 'Primeiros passos (Onboarding)',
    icon: Rocket,
    summary: 'O passo a passo guiado que aparece quando você cria a conta.',
    keywords: ['onboarding', 'novo', 'tutorial', 'primeiro acesso'],
    body: [
      { type: 'p', text: 'Quando você cria a conta, mostramos um guia em 4 passos para você sair do zero até ter a primeira TV exibindo conteúdo.' },
      { type: 'steps', items: [
        '**Bem-vindo** — uma visão rápida do que dá pra fazer no MyShow.',
        '**Conecte sua primeira TV** — como parear uma tela usando o código.',
        '**Escolha um tema** — defina a cara visual do seu conteúdo.',
        '**Pronto** — você é levado ao Dashboard para começar.',
      ] },
      { type: 'tip', text: 'Pode pular a qualquer momento clicando em "Ir pro dashboard". Você sempre consegue refazer cada passo depois pelas telas correspondentes.' },
    ],
  },

  // ─── LOJAS E TVs ──────────────────────────────────────────────────────────
  {
    id: 'stores',
    route: '/stores',
    section: 'Lojas e TVs',
    title: 'Lojas',
    icon: Building2,
    summary: 'Cadastre suas filiais e vincule TVs e produtos a cada uma.',
    keywords: ['loja', 'filial', 'unidade', 'multi-loja', 'cnpj'],
    tourId: 'stores',
    body: [
      { type: 'p', text: 'Aqui você cadastra as unidades do seu negócio. Cada TV e cada produto pode ser vinculado a uma loja, o que ajuda a organizar tudo quando você tem mais de um ponto.' },
      { type: 'heading', text: 'Como cadastrar' },
      { type: 'steps', items: [
        'Clique em **Nova loja**.',
        'Informe nome, código e dados de contato. O **código** deve bater com o código da loja no seu ERP, caso você use a integração.',
        'Salve. Use o ícone de estrela para definir a **loja padrão**.',
      ] },
      { type: 'terms', items: [
        { term: 'Loja padrão (default)', def: 'a filial usada automaticamente quando uma TV ou produto não tem loja específica.' },
        { term: 'Código da loja', def: 'identificador único; precisa coincidir com o ERP para a sincronização funcionar.' },
      ] },
      { type: 'warn', text: 'Não dá pra excluir a loja padrão. Promova outra loja a padrão antes. Se a loja tiver TVs vinculadas, o sistema pede para onde transferi-las.' },
      { type: 'tip', text: 'Trabalhar com várias lojas é um recurso do plano Enterprise. No plano base você opera com uma única loja.' },
    ],
  },
  {
    id: 'tvs',
    route: '/tvs',
    section: 'Lojas e TVs',
    title: 'TVs',
    icon: Tv,
    summary: 'Gerencie todas as suas telas: status, busca e filtros.',
    keywords: ['tv', 'tela', 'dispositivo', 'monitor', 'status', 'online', 'offline'],
    tourId: 'tvs',
    body: [
      { type: 'p', text: 'Esta é a central de todas as suas telas. Cada card mostra o status atual, a localização e a última vez que a TV deu sinal.' },
      { type: 'heading', text: 'Status das TVs' },
      { type: 'terms', items: [
        { term: 'Online', def: 'a TV enviou sinal nos últimos instantes (ponto pulsando em verde).' },
        { term: 'Offline', def: 'a TV está sem enviar sinal — verifique energia e internet.' },
        { term: 'Pareando', def: 'a TV está sendo conectada e ainda não concluiu.' },
        { term: 'Erro', def: 'a TV reportou um problema ao tocar o conteúdo.' },
      ] },
      { type: 'heading', text: 'Como usar' },
      { type: 'list', items: [
        'Busque pelo nome da TV no campo de busca.',
        'Filtre por **status** ou por **loja** (quando há mais de uma).',
        'Clique numa TV para abrir os detalhes e atribuir uma playlist.',
      ] },
      { type: 'warn', text: 'Se uma TV mostra "5+ reinícios em 24h", isso indica instabilidade — pode ser problema de energia, internet ou de uma playlist pesada demais para o aparelho.' },
    ],
  },
  {
    id: 'tvs-add',
    route: '/tvs/add',
    section: 'Lojas e TVs',
    title: 'Adicionar TV (pareamento)',
    icon: PlusCircle,
    summary: 'Conecte uma nova tela usando o código que aparece nela.',
    keywords: ['parear', 'pareamento', 'código', 'conectar', 'nova tv', 'adicionar'],
    tourId: 'tvs-add',
    body: [
      { type: 'p', text: 'Conectar uma TV é plug-and-play: você instala o app na tela, ele mostra um código, e você digita esse código aqui.' },
      { type: 'heading', text: 'Passo a passo' },
      { type: 'steps', items: [
        'Conecte a TV (ou box Android) à internet.',
        'Abra o app MyShow na tela.',
        'Anote o **código de pareamento** que aparece (8 caracteres, ex.: ABC12XYZ).',
        'Digite o código no campo desta tela.',
        '(Opcional) Dê um nome e uma localização para identificar a TV depois.',
        'Clique em **Parear TV**.',
      ] },
      { type: 'warn', text: 'O código expira em poucos minutos. Se der "código inválido ou expirado", basta pegar o novo código que a TV está mostrando.' },
      { type: 'tip', text: 'Dê nomes que façam sentido para você ("Vitrine", "Caixa 2", "Açougue") — fica muito mais fácil gerenciar várias telas depois.' },
    ],
  },
  {
    id: 'tvs-detail',
    routeMatch: /^\/tvs\/(?!add$|sessions$)[^/]+$/,
    section: 'Lojas e TVs',
    title: 'Detalhe da TV',
    icon: Settings2,
    summary: 'Edite a TV, atribua a playlist e veja as informações do aparelho.',
    keywords: ['detalhe', 'editar tv', 'atribuir playlist', 'hardware', 'dispositivo'],
    tourId: 'tvs-detail',
    body: [
      { type: 'p', text: 'Nesta tela você controla uma TV específica: muda o nome, atribui qual playlist ela exibe e consulta as informações técnicas do aparelho.' },
      { type: 'heading', text: 'O que dá pra fazer' },
      { type: 'steps', items: [
        'Clique no ícone de **editar** para alterar nome, localização e loja; depois clique em **Salvar**.',
        'No card **Playlist atual**, escolha a playlist no seletor e clique em **Salvar playlist** — a TV passa a exibir esse conteúdo.',
        'Use **Editar playlist** para abrir o editor do conteúdo que está no ar.',
      ] },
      { type: 'p', text: 'O card **Dispositivo** mostra marca, modelo, memória, versão do app e IP — reportados automaticamente pela própria TV. Útil para suporte e diagnóstico.' },
      { type: 'tip', text: 'O **Device ID** é o identificador único do aparelho. Tenha ele à mão se precisar falar com o suporte sobre uma TV específica.' },
    ],
  },
  {
    id: 'tvs-sessions',
    route: '/tvs/sessions',
    section: 'Lojas e TVs',
    title: 'Calendário de uso',
    icon: Calendar,
    summary: 'Veja quando cada TV ligou e desligou, dia a dia.',
    keywords: ['calendário', 'sessões', 'uso', 'histórico', 'timeline', 'ligou', 'desligou'],
    tourId: 'tvs-sessions',
    body: [
      { type: 'p', text: 'O Calendário de uso mostra uma linha do tempo de quando suas TVs estiveram ligadas. É a melhor ferramenta para entender padrões de uso e diagnosticar quedas.' },
      { type: 'heading', text: 'Como ler' },
      { type: 'list', items: [
        'Cada linha é um dia; as barras coloridas mostram os períodos em que cada TV ficou online.',
        'Cada cor representa uma TV (veja a legenda).',
        'O resumo no topo traz o total de sessões e o tempo total ligado.',
      ] },
      { type: 'heading', text: 'Filtros' },
      { type: 'steps', items: [
        'Escolha o período: **7d**, **30d** ou **90d**.',
        'Filtre por loja e/ou por TV específica para focar no que interessa.',
      ] },
      { type: 'tip', text: 'Se uma loja deveria estar aberta mas a barra está vazia naquele horário, é sinal de que a TV foi desligada ou perdeu a internet — combine com o Dashboard para confirmar.' },
    ],
  },

  // ─── CONTEÚDO ───────────────────────────────────────────────────────────────
  {
    id: 'playlists',
    route: '/playlists',
    section: 'Conteúdo',
    title: 'Playlists',
    icon: ListVideo,
    summary: 'Suas sequências de conteúdo que tocam nas TVs.',
    keywords: ['playlist', 'conteúdo', 'slides', 'sequência'],
    tourId: 'playlists',
    body: [
      { type: 'p', text: 'Uma playlist é uma sequência de slides (imagens, vídeos, tabelas de preço, promoções…) que tocam em loop na TV. Aqui ficam todas as suas playlists.' },
      { type: 'heading', text: 'Criar uma playlist' },
      { type: 'steps', items: [
        '**Usar modelo pronto** — comece de um template do seu segmento e só personalize. Mais rápido.',
        '**Em branco** — monte tudo do zero com total liberdade.',
      ] },
      { type: 'heading', text: 'Ações de cada playlist' },
      { type: 'list', items: [
        '**Editar** — abre o editor de slides.',
        '**Pré-visualizar** (ícone de olho) — veja como vai ficar antes de publicar.',
        '**Duplicar** — cria uma cópia para variar sem perder a original.',
        '**Excluir** — remove a playlist.',
      ] },
      { type: 'terms', items: [
        { term: 'Orientação', def: 'Horizontal (16:9, TV deitada) ou Vertical (9:16, totem em pé).' },
        { term: 'Ativa/Inativa', def: 'indica se a playlist pode ser atribuída às TVs.' },
      ] },
    ],
  },
  {
    id: 'playlist-editor',
    route: '/playlists/new',
    routeMatch: /^\/playlists\/(?!templates$)[^/]+$/,
    section: 'Conteúdo',
    title: 'Editor de playlist',
    icon: Wand2,
    summary: 'Monte os slides, escolha a orientação e o tema.',
    keywords: ['editor', 'slide', 'montar', 'orientação', 'arrastar', 'reordenar'],
    tourId: 'playlist-editor',
    body: [
      { type: 'p', text: 'O editor é onde você monta o conteúdo slide a slide. À esquerda fica a lista de slides; no centro, o editor do slide selecionado.' },
      { type: 'heading', text: 'Antes de tudo: a orientação' },
      { type: 'p', text: 'Escolha **Deitada (16:9)** para TVs na horizontal ou **Em pé (9:16)** para totens verticais. O banner no topo sempre mostra qual orientação você está editando.' },
      { type: 'warn', text: 'Defina a orientação certa logo no começo. Mudar depois de montar os slides pode quebrar o layout do conteúdo.' },
      { type: 'heading', text: 'Adicionar e organizar slides' },
      { type: 'steps', items: [
        'Use os botões de tipo de slide: **Imagem, Vídeo, Tabela de preços, Banner de promoção, Carrossel de produtos, HTML, Clima, Data/hora**.',
        'Clique num slide na lista à esquerda para editá-lo no centro.',
        'Arraste pelo ícone de alça (≡) para **reordenar** os slides.',
        'Ajuste a **duração** de cada slide (em segundos).',
        'Defina nome e tema e clique em **Salvar info**.',
      ] },
      { type: 'tip', text: 'O **Carrossel de produtos** e a **Tabela de preços** puxam automaticamente os produtos cadastrados — ótimo para quem usa a integração com o ERP: o preço atualiza sozinho.' },
    ],
  },
  {
    id: 'templates',
    route: '/playlists/templates',
    section: 'Conteúdo',
    title: 'Modelos prontos',
    icon: LayoutTemplate,
    summary: 'Playlists pré-montadas por segmento, prontas em 1 clique.',
    keywords: ['modelo', 'template', 'pronto', 'segmento', 'galeria'],
    tourId: 'templates',
    body: [
      { type: 'p', text: 'A galeria de modelos traz playlists já montadas por tipo de negócio (Açougue, Padaria, Mercado, Farmácia, Hortifruti). É o caminho mais rápido para começar.' },
      { type: 'steps', items: [
        'Filtre pelo seu **segmento** nos botões do topo.',
        'Clique em **Pré-visualizar** para ver os slides do modelo.',
        'Clique em **Usar** — o modelo vira uma playlist sua e abre direto no editor para você personalizar.',
      ] },
      { type: 'tip', text: 'Comece por um modelo e troque textos/imagens pelos seus. Você economiza horas em vez de montar do zero.' },
    ],
  },
  {
    id: 'themes',
    route: '/themes',
    section: 'Conteúdo',
    title: 'Temas',
    icon: Palette,
    summary: 'Paletas de cores e fontes prontas para o seu conteúdo.',
    keywords: ['tema', 'cores', 'fonte', 'visual', 'identidade', 'paleta'],
    tourId: 'themes',
    body: [
      { type: 'p', text: 'Temas são combinações prontas de cores e fontes que dão uma cara profissional ao seu conteúdo. Cada card mostra um preview com a paleta.' },
      { type: 'p', text: 'Os temas não são aplicados aqui diretamente — você os escolhe ao **criar ou editar uma playlist**, no seletor de tema. Esta tela serve para você conhecer as opções disponíveis.' },
      { type: 'terms', items: [
        { term: 'Cores do tema', def: 'primária, secundária, destaque e cor de promoção, aplicadas a todos os slides.' },
        { term: 'Tema Pro', def: 'alguns temas premium podem exigir um plano superior.' },
      ] },
    ],
  },

  // ─── PRODUTOS ─────────────────────────────────────────────────────────────
  {
    id: 'products',
    route: '/products',
    section: 'Produtos',
    title: 'Produtos',
    icon: Package,
    summary: 'Seu catálogo de preços e promoções para as telas.',
    keywords: ['produto', 'preço', 'promoção', 'catálogo', 'importar', 'csv', 'estoque'],
    tourId: 'products',
    body: [
      { type: 'p', text: 'O catálogo de produtos alimenta os slides de preço, tabela e carrossel. Você pode cadastrar manualmente, importar de um arquivo, ou sincronizar automaticamente com seu ERP via Agente.' },
      { type: 'heading', text: 'Formas de abastecer o catálogo' },
      { type: 'list', items: [
        '**Novo produto** — cadastro manual, um a um.',
        '**Importar CSV/Toledo** — sobe vários produtos de uma vez por arquivo.',
        '**Agente ETL** — sincroniza direto do seu sistema (a barra de status no topo mostra se está online e a última sincronização).',
      ] },
      { type: 'heading', text: 'Cadastrar / editar um produto' },
      { type: 'steps', items: [
        'Clique em **Novo produto** (ou numa linha da tabela para editar).',
        'Preencha código, descrição, departamento, categoria e preço.',
        'Para promoção: informe o **preço promocional** e as datas de início/fim.',
        'Use **Buscar imagens** para achar uma foto do produto automaticamente.',
        'Marque **Em destaque** para o produto aparecer primeiro nas telas.',
      ] },
      { type: 'warn', text: 'O preço promocional precisa ser menor que o preço normal, senão o cadastro não salva.' },
      { type: 'tip', text: 'Use os filtros por departamento, categoria e "Apenas promoções" para achar produtos rápido em catálogos grandes.' },
    ],
  },

  // ─── INTEGRAÇÃO (AGENTE ETL) ──────────────────────────────────────────────
  {
    id: 'agent',
    route: '/settings/agent',
    section: 'Integração (Agente ETL)',
    title: 'Agentes (sincronização com o ERP)',
    icon: ServerCog,
    summary: 'As máquinas que sincronizam seu sistema com o MyShow.',
    keywords: ['agente', 'agent', 'etl', 'sincronização', 'erp', 'instalar', 'token'],
    tourId: 'agent',
    body: [
      { type: 'p', text: 'O Agente é um programa instalado num computador da sua empresa que lê os dados do seu ERP (produtos, preços, promoções) e envia para o MyShow automaticamente. Esta tela lista os agentes e mostra a saúde de cada um.' },
      { type: 'heading', text: 'Instalar um agente' },
      { type: 'steps', items: [
        'Clique em **Instalar novo agente**.',
        'Copie o **token** gerado e baixe o instalador.',
        'Rode o instalador no computador que tem acesso ao banco do ERP e informe o token.',
        'O agente aparece em "Pendentes" e, ao conectar, passa para "Ativos".',
      ] },
      { type: 'terms', items: [
        { term: 'Online', def: 'deu sinal nos últimos 5 minutos.' },
        { term: 'Sem sinal (stale)', def: 'ficou entre 5 e 30 min sem responder.' },
        { term: 'Offline', def: 'mais de 30 min sem sinal — verifique o computador/serviço.' },
        { term: 'Token de instalação', def: 'credencial temporária para registrar um novo agente; expira após o uso.' },
      ] },
      { type: 'warn', text: 'Por segurança, o Agente nunca altera o banco do seu ERP — ele apenas lê os dados.' },
    ],
  },
  {
    id: 'agent-routines',
    route: '/settings/agent/routines',
    section: 'Integração (Agente ETL)',
    title: 'Rotinas de sincronização',
    icon: Repeat,
    summary: 'Os jobs agendados que buscam dados do ERP.',
    keywords: ['rotina', 'job', 'agendamento', 'sql', 'sincronizar', 'intervalo'],
    tourId: 'agent-routines',
    body: [
      { type: 'p', text: 'Cada rotina é uma tarefa agendada que busca um tipo de dado no ERP (produtos, promoções, estoque ou categorias) e atualiza o MyShow no intervalo definido.' },
      { type: 'steps', items: [
        'Veja o status de cada rotina: último resultado (OK, erro ou vazio) e quando rodou.',
        'Use **executar** (▶) para rodar uma rotina na hora, sem esperar o intervalo.',
        'Edite uma rotina para ajustar a consulta, o destino (produtos/promoções/estoque/categorias) e o intervalo.',
      ] },
      { type: 'terms', items: [
        { term: 'Destino (target)', def: 'o que a rotina atualiza: produtos, promoções, estoque ou categorias.' },
        { term: 'Intervalo', def: 'de quanto em quanto tempo a rotina roda (ex.: a cada 5 min).' },
        { term: 'Resultado "vazio"', def: 'a rotina rodou mas não trouxe linhas — pode indicar consulta sem dados no período.' },
      ] },
      { type: 'warn', text: 'Mexer na consulta (SQL) de uma rotina é uma operação técnica. Em caso de dúvida, fale com o suporte antes de alterar.' },
    ],
  },
  {
    id: 'agent-catalog',
    route: '/settings/agent/catalog',
    section: 'Integração (Agente ETL)',
    title: 'Sincronização de catálogo',
    icon: FolderSync,
    summary: 'Escolha quais categorias do ERP exibir nas telas.',
    keywords: ['catálogo', 'categorias', 'departamento', 'descobrir', 'gerar rotinas'],
    tourId: 'agent-catalog',
    body: [
      { type: 'p', text: 'Este assistente em 3 passos conecta o catálogo do seu ERP ao MyShow, deixando você escolher exatamente quais categorias de produto quer exibir.' },
      { type: 'steps', items: [
        '**Descobrir categorias** — o sistema lê a estrutura de departamentos e categorias do seu ERP.',
        '**Selecionar** — marque as categorias que você quer sincronizar e exibir.',
        '**Gerar rotinas** — o MyShow cria/atualiza as rotinas de produto já filtradas pelas categorias escolhidas.',
      ] },
      { type: 'tip', text: 'Sincronize só o que vai usar nas telas. Menos categorias = sincronização mais leve e rápida.' },
    ],
  },
  {
    id: 'agent-logs',
    route: '/settings/agent/logs',
    section: 'Integração (Agente ETL)',
    title: 'Histórico de execuções',
    icon: ScrollText,
    summary: 'Auditoria de todas as sincronizações que rodaram.',
    keywords: ['log', 'execução', 'histórico', 'erro', 'auditoria', 'debug'],
    tourId: 'agent-logs',
    body: [
      { type: 'p', text: 'Aqui ficam registradas todas as execuções das rotinas: quando rodaram, se deram certo, quantas linhas processaram e quanto tempo levaram.' },
      { type: 'list', items: [
        'Filtre por rotina e por status (sucesso, erro, vazio).',
        'Clique numa linha para ver a mensagem de erro completa, quando houver.',
      ] },
      { type: 'tip', text: 'Se um produto ou preço não atualizou nas telas, este é o primeiro lugar para investigar: veja se a rotina correspondente rodou com sucesso.' },
    ],
  },

  // ─── CONTA ────────────────────────────────────────────────────────────────
  {
    id: 'billing',
    route: '/billing',
    section: 'Conta',
    title: 'Cobrança',
    icon: CreditCard,
    summary: 'Seu plano, faturas, quantidade de TVs e pagamento.',
    keywords: ['cobrança', 'plano', 'fatura', 'pagamento', 'assinatura', 'trial', 'cartão'],
    tourId: 'billing',
    body: [
      { type: 'p', text: 'Nesta tela você gerencia sua assinatura: o plano atual, quantas TVs estão contratadas, as faturas e a forma de pagamento.' },
      { type: 'heading', text: 'O que dá pra fazer' },
      { type: 'list', items: [
        '**Mudar plano** — troca o tier da assinatura.',
        '**Ajustar TVs** — aumenta/diminui a quantidade de telas contratadas.',
        '**Ativar assinatura** — converte o teste gratuito em conta paga.',
        '**Pagar** — quita uma fatura pendente ou vencida; **sincronizar** (↻) atualiza o status da fatura.',
      ] },
      { type: 'terms', items: [
        { term: 'Em teste (trialing)', def: 'período gratuito de avaliação.' },
        { term: 'Ativo (active)', def: 'assinatura paga e em dia.' },
        { term: 'Em atraso (past due)', def: 'há fatura vencida — regularize para não perder o acesso.' },
        { term: 'Desconto por volume', def: 'quanto mais TVs, menor o valor por TV.' },
      ] },
      { type: 'warn', text: 'Se o pagamento ficar em atraso, o acesso pode ser suspenso. Use o botão **Pagar** assim que a fatura aparecer como vencida.' },
    ],
  },
  {
    id: 'settings',
    route: '/settings',
    section: 'Conta',
    title: 'Configurações',
    icon: Settings,
    summary: 'Dados da empresa, usuários, categorias, notificações e API.',
    keywords: ['configurações', 'empresa', 'usuários', 'convidar', 'notificações', 'api', 'timezone'],
    tourId: 'settings',
    body: [
      { type: 'p', text: 'O hub de configurações da conta, organizado em abas:' },
      { type: 'list', items: [
        '**Empresa** — razão social, contato, fuso horário, idioma e logo.',
        '**Usuários** — convide pessoas da equipe e defina o papel (admin ou editor).',
        '**Categorias / Departamentos** — taxonomia dos seus produtos.',
        '**Agente ETL** — atalho para a integração com o ERP.',
        '**Notificações** — escolha quais alertas você quer receber.',
        '**API** — token de acesso e documentação para integrações próprias.',
      ] },
      { type: 'tip', text: 'Convide sua equipe em **Usuários**: o papel **editor** pode montar conteúdo sem acesso à parte financeira.' },
    ],
  },
];

/** Encontra o artigo que corresponde a uma rota (para a ajuda contextual). */
export function findArticleByPath(path: string): HelpArticle | undefined {
  // primeiro tenta rota exata, depois regex
  return (
    ARTICLES.find((a) => a.route === path) ||
    ARTICLES.find((a) => a.routeMatch?.test(path))
  );
}
